import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import BackToTop from '../components/BackToTop';

const API_URL = '/api/content';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const res = await fetch(`${API_URL}/projects`);
      const data = await res.json();
      const projects = Array.isArray(data) ? data : [];
      const found = projects.find(p => p.id === parseInt(id) || p.id === id);
      setProject(found || null);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Project not found</h1>
          <Link to="/" className="text-pink-600 hover:text-pink-700">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BackToTop />

      {/* Hero Image */}
      {project.image && (
        <div className="w-full h-64 md:h-96 overflow-hidden bg-gray-200">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Project Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-pink-600 hover:text-pink-700 mb-8">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Projects
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-pink-100 text-pink-700 text-sm font-medium rounded-full">
              {project.category || 'Project'}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {project.title}
          </h1>
          <p className="text-lg text-gray-600">
            {project.excerpt || project.description}
          </p>
        </header>

        {/* Project Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 uppercase mb-1">Members</p>
            <p className="text-base font-semibold text-gray-800">{project.members || 'N/A'}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 uppercase mb-1">Software Used</p>
            <p className="text-base font-semibold text-pink-600">{project.software || 'N/A'}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 uppercase mb-1">Market</p>
            <p className="text-base font-semibold text-gray-800">{project.market || 'N/A'}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 uppercase mb-1">Timeline</p>
            <p className="text-base font-semibold text-gray-800">{project.timeline || 'N/A'}</p>
          </div>
        </div>

        {/* Full Content */}
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Details</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            {project.content ? (
              project.content.startsWith('<') || project.content.includes('<p') || project.content.includes('<h') || project.content.includes('<ul') || project.content.includes('<ol') ? (
                <div dangerouslySetInnerHTML={{ __html: project.content }} />
              ) : (
                project.content.split('\n').map((paragraph, index) => (
                  paragraph.trim() && <p key={index} className="mb-4">{paragraph}</p>
                ))
              )
            ) : (
              <p className="text-gray-700 whitespace-pre-line">{project.description}</p>
            )}
          </div>
        </div>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-600 mb-4">Share this project:</p>
          <div className="flex gap-4">
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(project.title)}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-pink-600">
              Twitter
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-pink-600">
              Facebook
            </a>
          </div>
        </div>
      </article>
    </div>
  );
};

export default ProjectDetail;
